<template>
  <div class="flex flex-column gap-4">
    <div class="flex">
      <div class="flex gap-2">
        <b-button
          size="sm"
          variant="primary"
          class="text-nowrap"
          @click="createGroup()"
        >
          Add new group
        </b-button>
      </div>
    </div>

    <div class="font-bold">
      Showing {{ groups.length | format }} group(s)
    </div>

    <div class="max-w-xs">
      <b-form-input
        v-model="searchText"
        size="sm"
        type="search"
        trim
        placeholder="Search in Key, Name, Extras"
        autofocus
        autocomplete="off"
      >
      </b-form-input>
    </div>

    <table class="table table-google table-highlight">
      <thead>
        <tr>
          <th>ID</th>
          <th class="text-nowrap">Key</th>
          <th class="text-nowrap">Name</th>
          <th class="text-nowrap">Extras</th>
          <th class="text-nowrap">Short</th>
          <th class="text-nowrap">Sorting</th>
          <th class="text-nowrap">Prefix</th>
          <th class="text-nowrap">Suffix</th>
          <th class="text-nowrap">Options</th>
          <th>Action</th>
        </tr>
      </thead>

      <tbody>
        <tr v-if="groups.length === 0">
          <td colspan="8" class="font-bold">
            <div v-if="isLoading">Loading..</div>
            <div v-else>No groups available</div>
          </td>
        </tr>

        <tr v-for="group in filteredGroups" :key="group.id">
          <td class="row-fit">{{ group.id }}</td>
          <td class="row-fit">{{ group.group_key }}</td>
          <td class="text-nowrap">{{ group.group_name }}</td>
          <td class="row-fit text-nowrap">
            {{ group.extra_titles }}
          </td>
          <td class="row-fit">{{ group.require_short ? 'Yes' : '' }}</td>
          <td class="row-fit">{{ group.option_sort ? 'Automatic' : 'Manual' }}</td>
          <td class="row-fit text-nowrap">{{ group.option_prefix }}</td>
          <td class="row-fit text-nowrap">{{ group.option_suffix }}</td>
          <td class="row-fit">{{ group.option_count | format }}</td>
          <td class="row-fit">
            <div class="d-flex gap-2">
              <b-btn
                size="sm"
                variant="primary"
                :disabled="isLoading"
                @click="editGroup(group)"
              >
                Group
              </b-btn>
              <b-btn
                size="sm"
                variant="primary"
                :disabled="isLoading"
                @click="editOptions(group)"
              >
                Options
              </b-btn>
              <b-btn
                v-if="group.has_purchase"
                size="sm"
                variant="primary"
                :disabled="isLoading"
                @click="editPurchases(group)"
              >
                Purchases
              </b-btn>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <dropdown-creator
      v-if="createModal.showModal"
      :data="createModal"
      @created="onCreated"
    />

    <dropdown-group-editor
      v-if="groupModal.showModal"
      :data="groupModal"
      @updated="onUpdated"
      @deleted="onDeleted"
    />

    <dropdown-options-editor
      v-if="optionsModal.showModal"
      :data="optionsModal"
      @updated="onUpdated"
      @deleted="onDeleted"
    />

    <dropdown-purchases-editor
      v-if="purchasesModal.showModal"
      :data="purchasesModal"
    />
  </div>
</template>

<script>
const DropdownCreator = () => import('@/components/developer/DropdownCreator.vue');
const DropdownGroupEditor = () => import('@/components/developer/DropdownGroupEditor.vue');
const DropdownOptionsEditor = () => import('@/components/developer/DropdownOptionsEditor.vue');
const DropdownPurchasesEditor = () => import('@/components/developer/DropdownPurchasesEditor.vue');

export default {
  name: 'Dropdowns',
  components: {
    DropdownCreator,
    DropdownGroupEditor,
    DropdownOptionsEditor,
    DropdownPurchasesEditor,
  },
  computed: {
    isLoading() {
      return this.loadingCount > 0;
    },
    filteredGroups() {
      if (this.searchText.length === 0) {
        return this.groups;
      }
      const searchText = this.searchText.toLowerCase();
      return this.groups.filter((group) => {
        const keyMatch = group.group_key.toLowerCase().includes(searchText);
        if (keyMatch) {
          return true;
        }
        const nameMatch = group.group_name.toLowerCase().includes(searchText);
        if (nameMatch) {
          return true;
        }
        const extraMatch = group.extra_titles.toLowerCase().includes(searchText);
        if (extraMatch) {
          return true;
        }
        return false;
      });
    },
  },
  data() {
    return {
      searchText: '',
      loadingCount: 0,
      groups: [],
      createModal: {
        showModal: false,
      },
      groupModal: {
        showModal: false,
        groupKey: null,
      },
      optionsModal: {
        showModal: false,
        groupKey: null,
      },
      purchasesModal: {
        showModal: false,
        groupKey: null,
      },
    };
  },
  methods: {
    fetchGroups() {
      this.loadingCount++;
      this.$http
        .get('/select/groups')
        .then((res) => {
          this.groups = res.body.groups;
        })
        .catch((err) => {
          this.$toast.error(`Failed to fetch groups: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    createGroup() {
      this.createModal.showModal = true;
    },
    editGroup(group) {
      this.groupModal.groupKey = group.group_key;
      this.groupModal.showModal = true;
    },
    editOptions(group) {
      this.optionsModal.groupKey = group.group_key;
      this.optionsModal.showModal = true;
    },
    editPurchases(group) {
      this.purchasesModal.groupKey = group.group_key;
      this.purchasesModal.showModal = true;
    },
    onCreated(group) {
      this.fetchGroups();
      this.editGroup(group);
    },
    onUpdated() {
      this.fetchGroups();
    },
    onDeleted(groupKey) {
      this.groups = this.groups.filter((group) => group.group_key !== groupKey);
    },
  },
  created() {
    this.fetchGroups();
  },
};
</script>

<style lang="scss" scoped>
</style>
